import React, { useEffect, useState } from "react";
import { Bell, BellOff, ExternalLink, Check } from "lucide-react";
import { fireMochiNotification } from "@/lib/mochiNotifications";

function inIframe() {
    try {
        return window.self !== window.top;
    } catch {
        return true;
    }
}

export default function NotificationControl() {
    const [perm, setPerm] = useState(() =>
        typeof window !== "undefined" && "Notification" in window ? Notification.permission : "unsupported"
    );
    const framed = inIframe();
    const supported = perm !== "unsupported";

    useEffect(() => {
        if (!supported) return;
        const handler = () => setPerm(Notification.permission);
        document.addEventListener("visibilitychange", handler);
        return () => document.removeEventListener("visibilitychange", handler);
    }, [supported]);

    const enable = async () => {
        if (!supported) return;
        try {
            const result = await Notification.requestPermission();
            setPerm(result);
            if (result === "granted") {
                fireMochiNotification("permission_granted", {}, { tag: "mochi-test" });
            }
        } catch (e) {
            setPerm(Notification.permission);
        }
    };

    const openInNewTab = () => {
        try {
            window.open(window.location.href, "_blank", "noopener,noreferrer");
        } catch (e) {/* ignore */}
    };

    let label = "Off";
    let tone = "text-warm-500";
    let Icon = BellOff;
    if (perm === "granted") { label = "On"; tone = "text-sage"; Icon = Bell; }
    else if (perm === "denied") { label = "Blocked"; tone = "text-terracotta"; Icon = BellOff; }
    else if (perm === "unsupported") { label = "Not supported"; tone = "text-warm-500"; Icon = BellOff; }

    return (
        <div className="mb-5" data-testid="settings-notif-control">
            <p className="font-body text-xs uppercase tracking-widest text-warm-500 mb-2">Browser notifications</p>
            <div className="bg-surface rounded-2xl p-3.5 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full bg-cream flex items-center justify-center shrink-0 ${tone}`}>
                    <Icon size={18} />
                </div>
                <div className="flex-1 min-w-0">
                    <p className={`font-display text-sm ${tone}`}>{label}</p>
                    <p className="font-body text-xs text-warm-500">
                        {perm === "granted" && "You'll see popups even when this tab is in the background."}
                        {perm === "default" && "Click Enable to let Mochi ping you on the desktop."}
                        {perm === "denied" && "Allow notifications in your browser's site settings, then refresh."}
                        {perm === "unsupported" && "This browser doesn't support notifications."}
                    </p>
                </div>
                {perm === "default" && (
                    <button
                        onClick={enable}
                        className="bg-terracotta hover:bg-terracotta-hover text-cream font-display text-xs px-3 py-2 rounded-full inline-flex items-center gap-1 transition-all active:scale-95 shrink-0"
                        data-testid="notif-enable"
                    >
                        <Bell size={12} /> Enable
                    </button>
                )}
                {perm === "granted" && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-display text-sage shrink-0">
                        <Check size={12} /> ready
                    </span>
                )}
            </div>

            {framed && perm !== "granted" && (
                <button
                    onClick={openInNewTab}
                    className="mt-2 w-full bg-cream border border-warm-300/40 hover:bg-surface text-warm-800 font-display text-xs py-2 rounded-2xl inline-flex items-center justify-center gap-1.5 transition-all"
                    data-testid="notif-open-newtab"
                >
                    <ExternalLink size={12} /> Open Mochi in a new tab to enable notifications
                </button>
            )}

            {framed && (
                <p className="font-body text-[11px] text-warm-500 mt-2 leading-snug">
                    Tip: browsers often block notifications inside embedded previews. If Enable doesn't work, open Mochi in a fresh browser tab.
                </p>
            )}
        </div>
    );
}
